import "@/components/Layout/Table/TableRow/TableRow.scss";
import "./DeviceListRow.scss";

import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ArrowRight, ExpiresFlag } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { HintTooltip } from "@/components/Interface/HintTooltip/HintTooltip";
import { AdditionalColumnMap } from "@/components/Portal/components/PortalDeviceListFilter/types";
import { ExtendedSimCardDetail } from "@/hooks/useExtendedSims";
import { useNeedsIDCheck } from "@/hooks/useNeedsIDCheck";
import {
  getFlatNameFromDevice,
  getFlatNameFromLocalFlatSpeed,
  getSimTypeLabel,
  hasAllowedManagement,
  isNumberPortingTile,
} from "@/utils/deviceUtils";

import { DeviceStatusIndicator } from "./DeviceStatusIndicator/DeviceStatusIndicator";
import { RoamingColumns } from "./RoamingColumns/RoamingColumns";

interface Props {
  additionalColumns?: AdditionalColumnMap;
  device: ExtendedSimCardDetail;
  manageRoute: string;
}

export const DeviceListRow: FC<Props> = ({
  additionalColumns,
  device,
  manageRoute,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const needsIdCheck = useNeedsIDCheck();

  const navigateToDevice = () => {
    navigate(`${manageRoute}?simId=${device.simId}`);
  };

  const { telephoneNumber, iccid } = additionalColumns || {};

  const isManageable = hasAllowedManagement(device) && !needsIdCheck;

  return (
    <tr
      role="link"
      aria-label={`Navigate to ${device.alias}`}
      {...(isManageable ? { onClick: navigateToDevice } : {})}
      className={clsx(
        "row-layout font-small",
        isManageable && "cursor-pointer",
      )}
    >
      <td data-testid="td-status" className="relative w-1 pl-6">
        {isNumberPortingTile(device) && (
          <>
            <div className="number-porting-flag">
              <ExpiresFlag className="" />
            </div>
            <div className="number-porting-flag-text">
              {t("cockpit.deviceList.row.orange")}
            </div>
          </>
        )}
        {/* The row navigates on click, so the status trigger has to keep its
            own tap to itself - otherwise touch users leave the page instead of
            reading the status. The stop sits outside HintTooltip so its own
            toggle still fires. */}
        <div onClick={(event) => event.stopPropagation()}>
          <HintTooltip
            content={t(`cockpit.deviceList.row.${device.simStatus ?? "green"}`)}
          >
            {/* A button, not the bare indicator: the tooltip has to be
                reachable by keyboard, and a span cannot take focus. */}
            <button
              type="button"
              aria-label={t("common.device.attributes.status")}
              className="flex items-center"
            >
              <DeviceStatusIndicator
                color={device.simStatus}
                className="ml-2 align-middle"
              />
            </button>
          </HintTooltip>
        </div>
      </td>
      <td data-testid="td-device-name">{device.alias}</td>
      <td data-testid="td-device-simType">
        {getSimTypeLabel(device.simType, t)}
      </td>
      <td data-testid="td-device-flat">{getFlatNameFromDevice(device)}</td>
      <td data-testid="td-voice-option">
        {device.voiceOption ? t("cockpit.deviceList.row.active") : "-"}
      </td>
      <td data-testid="td-runtime">
        {device.remainingDays
          ? `${device.remainingDays}/${device.minContractDuration} ${t(
              "Common.days",
              {
                ns: "translation",
              },
            )}`
          : "-"}
      </td>
      <td data-testid="td-followups">
        {getFlatNameFromLocalFlatSpeed(
          device.successorForRenew?.description?.split("/")[0] || "-",
        )}
      </td>
      {/*Optional Columns*/}
      {additionalColumns && (
        <RoamingColumns device={device} additionalColumns={additionalColumns} />
      )}
      {telephoneNumber && <td data-testid="td-number">{device.msisdn}</td>}
      {iccid && <td data-testid="td-ICCID">{device.iccid}</td>}
      <td className="icon-cell">
        <div className="flex justify-center">
          <Button
            dataTestid="device-list-row-manage"
            ariaLabel={t("common.device.attributes.manageDevice", {
              ns: "portal",
            })}
            variant="secondary"
            size="sm"
            icon={<ArrowRight className="size-4" />}
            disabled={!isManageable}
            // The whole row already navigates; without this the click runs
            // twice, once here and once on the row behind it.
            onClick={(event) => {
              event.stopPropagation();
              navigateToDevice();
            }}
          />
        </div>
      </td>
    </tr>
  );
};
